import { useEffect, useState } from 'react'

// localStorage-backed dark-mode hook. Single source of truth so the toggle
// survives navigation between `/` and `/distribution` (it didn't pre-Phase-19,
// because each top-level component owned its own [dark, setDark]).

const STORAGE_KEY = 'odds-portal-theme'

export type Theme = 'light' | 'dark'

function readInitial(): Theme {
  if (typeof window === 'undefined')
    return 'light'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light')
      return stored
  }
  catch {
    /* localStorage unavailable */
  }
  return 'light'
}

export function useTheme(): { theme: Theme, toggle: () => void, isDark: boolean } {
  const [theme, setTheme] = useState<Theme>(readInitial)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    }
    catch {
      /* ignore */
    }
  }, [theme])

  // Cross-tab sync: another tab toggling fires a `storage` event here.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'dark' || e.newValue === 'light')) {
        setTheme(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return {
    theme,
    isDark: theme === 'dark',
    toggle: () => setTheme(t => (t === 'dark' ? 'light' : 'dark')),
  }
}
